import WestIcon from '@mui/icons-material/West';
import CircularProgress from "@mui/material/CircularProgress";
import { useEffect, useRef, useState } from "react"; // Import useRef
import { useLocation, useParams } from "react-router-dom";
import EmptySetIcon from "../../../../animation/jobs/Empty state job.svg";
import mockFilterOptions from "../../mockFilter.json";
import { ServiceApi } from '../../Service/ServiceApi';
import FilterBar from "../FilterBar/FilterBar";
import FilterDrawer from "../FilterBar/FilterDrawer";
import JobCountBar from "../JobBar/JobCountBar";
import JobDetail from "../JobDetail/JobDetail";
import JobList from "../JobList/JobList";
import styles from "./JobPage.module.css";

// Returns offset like "+05:30" for the Date object
const getTimezoneOffsetString = (date = new Date()) => {
  const offsetMinutes = -date.getTimezoneOffset(); // - because getTimezoneOffset is opposite sign
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const abs = Math.abs(offsetMinutes);
  const hours = String(Math.floor(abs / 60)).padStart(2, "0");
  const minutes = String(abs % 60).padStart(2, "0");
  return `${sign}${hours}:${minutes}`;
};

const toLocalIsoWithOffset = (date = new Date()) => {
  const isoNoZ = date.toISOString().replace("Z", "");
  const offset = getTimezoneOffsetString(date);
  return `${isoNoZ}${offset}`;
};

const computeCreatedAfterFromOption = (optionValue) => {
  if (!optionValue || optionValue === "Any Time" || optionValue === "Any time") return "";

  const now = new Date();
  let date;

  switch (optionValue) {
    case "Past 24 hours":
      date = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      break;
    case "Past Week":
    case "Past week":
      date = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      break;
    case "Past Month":
    case "Past month":
      date = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      break;
    default:
      try {
        const parsed = new Date(optionValue);
        if (!isNaN(parsed)) return toLocalIsoWithOffset(parsed);
      } catch (e) { }
      return "";
  }

  return toLocalIsoWithOffset(date);
};

function JobPage() {
  const [jobsData, setJobsData] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const { id } = useParams();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const fromMain = urlParams.get("from") === "main";
  const relatedTitleParam = urlParams.get("related_title") || "";
  const [searchQuery, setSearchQuery] = useState("react js in Indiana, United States");
  const [results, setResults] = useState(0);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState({});
  const [filterCategories, setFilterCategories] = useState([
    "Date Posted", "Experience level", "Company", "Job type", "Remote", "Industry", "Job function"
  ]);

  const categoryMap = {
    "Date posted": "datePosted",
    "Experience level": "experienceLevel",
    "Job type": "jobtype",
    "Company": "company",
    "Remote": "remote",
    "Search Jobs": "searchjobs",
    "Location": "location",
    "Industry": "industry",
    "Job function": "jobFunction",
  };

  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const [showJobDetail, setShowJobDetail] = useState(false);

  // 1. Create a ref for the scrolling container
  const jobsListRef = useRef(null);

  const token = localStorage.getItem("token")
 


  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        setFilterOptions(mockFilterOptions);
        if (relatedTitleParam) {
          setSelectedFilters({ ...selectedFilters, searchjobs: relatedTitleParam });
        }
        await fetchJobs();
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
  }, []);


  // Fetch jobs
  const fetchJobs = async (url, append = false) => {
    try {
      setLoading(true);
      const result = await ServiceApi.fetchjob(token,url);
    setJobsData(prevJobs => {
     
      let updatedJobs = append
        ? [...prevJobs, ...(result.data || [])]
        : [...(result.data || [])];

      
      if (id) {
        const found = updatedJobs.find(item => item.id == id);
        if (found) {
          updatedJobs = [
            found,
            ...updatedJobs.filter(item => item.id != id),
          ];
        }
      }

      return updatedJobs;
    });

    
           

      setPagination({
        count: result.count,
        next: result.next,
        previous: result.previous,
      });
      if (url) {
        const urlObj = new URL(url);
        const offset = Number(urlObj.searchParams.get("offset") || 0);
        const limit = Number(urlObj.searchParams.get("limit") || 10);
        setCurrentPage(Math.floor(offset / limit) + 1);
      } else {
        setCurrentPage(1);
      }
    } catch (err) {
      console.error("Error fetching jobs:", err);
      setError("Failed to fetch jobs. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetch = async () => {
      try {
        if (jobsData.length > 0) {
          if (id) {
            const a = await ServiceApi.JobViewApi(token, id);
            setSelectedJob(a || jobsData[0]);
          } else {
            setSelectedJob(jobsData[0]);
          }
        } else {
          setSelectedJob(null);
        }
      }
      catch (err) {
        console.error("Error fetching jobs:", err);
        setError("Failed to fetch jobs. Please try again.");
      }
    }

    fetch();
  }, [id, jobsData]);

  const buildFilterBody = (filters) => {
    const datePostedValue = filters.datePosted || filters["Date posted"] || filters["Date Posted"] || "";
    const created_after = computeCreatedAfterFromOption(datePostedValue);

    return {
      skills: [],
      title: filters.searchjobs || "",
      company_name: filters.company || [],
      salary: "",
      job_type: filters.jobtype || filters.jobType || [],
      location: filters.location || "",
      remote: filters.remote || [],
      experience: filters.experienceLevel || filters.experience || "",
      job_function: filters.jobFunction || [],
      industry: filters.industry || [],
      created_after: created_after,
      created_before: "",
    };
  };



  // New: unified page-change handler that respects filters
  const handlePageChange = async (pageOrUrl, append = false) => {
    try {
      setLoading(true);
      let offset = 0;
      let limit = 10;
      let pageNum = null;
      if (typeof pageOrUrl === "string" && pageOrUrl.startsWith("http")) {
        try {
          const u = new URL(pageOrUrl);
          offset = Number(u.searchParams.get("offset") || 0);
          limit = Number(u.searchParams.get("limit") || 10);
          pageNum = Math.floor(offset / limit) + 1;
        } catch (e) {
          // fallback: leave offset/limit defaults
        }
      } else {
        // assume it's a page number
        pageNum = Number(pageOrUrl) || 1;
        limit = 10; // same as jobsPerPage in JobList
        offset = (pageNum - 1) * limit;
      }

      const hasActiveFilters = selectedFilters && Object.keys(selectedFilters).length > 0;

      if (hasActiveFilters) {
        const body = buildFilterBody(selectedFilters);
        const result = await ServiceApi.filterApi(token, body, offset, limit);

        setJobsData(prev => append ? [...prev, ...(result.data || [])] : (result.data || []));
        setPagination({
          count: result.count,
          next: result.next,
          previous: result.previous,
        });



        if (pageNum !== null) setCurrentPage(pageNum);
        else if (result.next || result.previous) {
        }
      } else {
        if (typeof pageOrUrl === "string" && pageOrUrl.startsWith("http")) {
          await fetchJobs(pageOrUrl, append);
        } else {
          const url = `${process.env.REACT_APP_API_BASE_URL}api/v1/jobs/?limit=${limit}&offset=${offset}`;
          await fetchJobs(url, append);
        }
      }
    } catch (err) {
      console.error("Pagination error:", err);
      setError("Failed to change page. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  const updateResults = async (filters) => {
    // Save UI-level selected filters
    setSelectedFilters(filters);

    try {
      setLoading(true);

  
      const datePostedValue = filters.datePosted || filters["Date posted"] || filters["Date Posted"] || "";

      
      const created_after = computeCreatedAfterFromOption(datePostedValue);

 
      const body = {
        skills: [],
        title: filters.searchjobs || "",
        company_name: filters.company || [],
        salary: "",
        job_type: filters.jobtype || filters.jobType || [],
        location: filters.location || "",
        remote: filters.remote || [],
        experience: filters.experienceLevel || filters.experience || "",
        job_function: filters.jobFunction || [],
        industry: filters.industry || [],
        created_after: created_after, 
        created_before: "", 
      };

      const result = await ServiceApi.filterApi(token, body);
      setJobsData(result.data || []);
      setPagination({
        count: result.count,
        next: result.next,
        previous: result.previous,
      });
    } catch (err) {
      console.error("Filter error:", err);
      setError("Failed to filter jobs. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  const handleJobSelect = (job) => {
    setSelectedJob(job);
    if (isMobileView) setShowJobDetail(true);
  };

  const closeJobDetail = () => {
    setShowJobDetail(false);
  };


  useEffect(() => {
    const container = jobsListRef.current;
    if (!container || !isMobileView) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 100;
      if (isAtBottom && pagination?.next && !loading) {
        fetchJobs(pagination.next, true); // `true` ensures new jobs are appended
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [pagination?.next, loading, isMobileView]);

  return (
    <div className={styles.LinkedinLikeJobPage}>
      <div className={styles.jobsContainer}>
        <FilterBar
          filterCategories={filterCategories}
          filterOptions={filterOptions.length ? filterOptions : mockFilterOptions}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          updateResults={updateResults}
          onOpen={() => setDrawerOpen(true)}
          categoryMap={categoryMap}
          fetchJobs={fetchJobs}
        />
        <FilterDrawer
          filterOptions={filterOptions.length ? filterOptions : mockFilterOptions}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          updateResults={updateResults}
          isOpen={isDrawerOpen}
          onClose={() => setDrawerOpen(false)}
          categoryMap={categoryMap}
          fetchJobs={fetchJobs}
        />
        <div className={styles.contentContainer}>
          <JobCountBar
            pagination={pagination}
            selectedFilters={selectedFilters}
            updateResults={updateResults}
          />
        </div>
        {jobsData.length === 0 && !loading ? (
          <div className={styles.emptySetWrapper}>
            <img src={EmptySetIcon} alt="No jobs found" className={styles.emptySetIcon} />
          </div>
        ) : (
          <>
            <div className={styles.left}>
              {/* 3. Assign the ref to the correct scrolling div */}
              <div className={styles.jobsList} ref={jobsListRef}>
                {jobsData.length === 0 ? (
                  ""
                ) : (
                  (!isMobileView || !showJobDetail) && (
                    <div className={styles.jobList}>
                      <JobList
                        jobs={jobsData}
                        onJobSelect={handleJobSelect}
                        selectedJobId={selectedJob?.id}
                        pagination={pagination}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                        loading={loading}
                      />
                    </div>
                  )
                )}
              </div>
              {isMobileView && (
                <div className={`${styles.jobDrawer} ${showJobDetail ? styles.open : ""}`}>
                  <button className={styles.closeButton} onClick={closeJobDetail}>
                    <WestIcon />
                  </button>
                  {loading ? (
                    <div className={styles.loaderWrapper}>
                      <CircularProgress style={{ color: "#F33070" }} size={60} thickness={5} />
                    </div>
                  ) : selectedJob ? (
                    <JobDetail job={selectedJob} />
                  ) : (
                    <div className={styles.noJobSelected}>Select a job to view details</div>
                  )}
                </div>
              )}
              {!isMobileView && (
                <div className={styles.jobDetail}>
                  {loading ? (
                    <div className={styles.loaderWrapper}>
                      <CircularProgress style={{ color: "#F33070" }} size={60} thickness={5} />
                    </div>
                  ) : selectedJob ? (
                    <JobDetail job={selectedJob} />
                  ) : (
                    <div className={styles.noJobSelected}>Select a job to view details</div>
                  )}
                </div>
              )}
            </div>
          </>
        )}

      </div>

    </div>
  );
}

export default JobPage;